import { Commit, Dispatch } from 'vuex'

import ErrorEntities from '@/models/error-entities'

import axios from '@/axios-module'
import router from '@/router'

import { AuthMutationTypes } from './auth/AuthMutationTypes'
import { CustomerMutationTypes } from './customer'

declare let FB: fb.FacebookStatic

interface State {
  initiated: boolean
  errors: ErrorEntities
}

const initialState: State = {
  initiated: false,
  errors: {},
}

export enum FacebookActionTypes {
  LoadSdk = '[Facebook] LoadSdk',
  Init = '[Facebook] Init',
  Login = '[Facebook] Login',
  Connect = '[Facebook] Connect',
  Disconnect = '[Facebook] Disconnect',
}

export enum FacebookMutationTypes {
  Init = '[Facebook] Init',
  LoginFailure = '[Facebook] LoginFailure',
}

export default {
  state: initialState,
  actions: {
    [FacebookActionTypes.LoadSdk]({
      dispatch,
    }: {
      dispatch: Dispatch
    }): Promise<void> {
      const src = 'https://connect.facebook.net/en_US/sdk.js'
      return new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve()

          return
        }

        const el: HTMLScriptElement = document.createElement('script')

        el.type = 'text/javascript'
        el.async = true
        el.src = src

        el.addEventListener('load', resolve as (ev: any) => void)
        el.addEventListener('error', reject)
        el.addEventListener('abort', reject)

        document.head.appendChild(el)
      }).then(() => dispatch(FacebookActionTypes.Init))
    },
    [FacebookActionTypes.Init]({ commit }: { commit: Commit }): void {
      FB.init({
        appId: `${process.env.VUE_APP_FB_APP_ID}`,
        cookie: true,
        xfbml: true,
        version: 'v3.2',
      })

      FB.AppEvents.logPageView()

      commit(FacebookMutationTypes.Init)
    },
    [FacebookActionTypes.Login](
      { commit }: { commit: Commit },
      redirectTo = 'dashboard',
    ) {
      commit(AuthMutationTypes.Login)
      FB.login(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (fbResponse: any) => {
          if (fbResponse.status !== 'connected') {
            commit(AuthMutationTypes.LoginFailure, 'Login with Facebook failed')
          }

          try {
            const response = await axios.get(
              '/customer/auth/facebook/callback',
              { params: { login: true } },
            )
            commit(AuthMutationTypes.LoginSuccess, response.data)
            commit(CustomerMutationTypes.GetCurrentSuccess, response.data)
            router.push({ name: redirectTo })
          } catch (error) {
            commit(FacebookMutationTypes.LoginFailure, error.response.data)
            commit(AuthMutationTypes.LoginFailure, 'Login with Facebook failed')
          }
        },
        { scope: 'email' },
      )
    },
    [FacebookActionTypes.Connect](
      { commit }: { commit: Commit },
      redirectTo = 'dashboard',
    ) {
      FB.login(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (fbResponse: any) => {
          if (fbResponse.status !== 'connected') {
            commit(FacebookMutationTypes.LoginFailure, {
              auth_error: 'Login with Facebook failed',
            })
          }

          try {
            const response = await axios.get(
              '/customer/auth/facebook/callback',
              { params: { connect: true } },
            )
            commit(CustomerMutationTypes.GetCurrentSuccess, response.data)
            router.push({ name: redirectTo })
          } catch (error) {
            commit(FacebookMutationTypes.LoginFailure, error.response.data)
          }
        },
        { scope: 'email' },
      )
    },
    async [FacebookActionTypes.Disconnect]({ commit }: { commit: Commit }) {
      await axios.post('/customer/auth/facebook/disconnect')
      return commit(CustomerMutationTypes.DisconnectFacebookSuccess)
    },
  },
  mutations: {
    [FacebookMutationTypes.Init](state: State) {
      state.initiated = true
    },
    [FacebookMutationTypes.LoginFailure](state: State, errors: ErrorEntities) {
      state.errors = errors
    },
  },
}
